/**
 * Dinero (ADR-005).
 *
 * Todo importe es un **entero en centesimos de UYU**. `$800` es `80000`. No existe
 * ningun `float` en este modulo: las unicas operaciones son sumas, restas y divisiones
 * enteras con resto explicito.
 *
 * Convencion de signo (ADR-017), valida en todo el sistema:
 *   - positivo  → el cliente **debe** mas (cargo)
 *   - negativo  → el cliente debe menos (pago, reversion, saldo a favor)
 */
import { errorDominio } from '../tipos/errores.js';
import { exito, fallo } from '../tipos/resultado.js';
/**
 * Cota de seguridad. Muy por encima de cualquier importe real del negocio
 * (una reserva cara son 130000 centesimos), y muy por debajo de `MAX_SAFE_INTEGER`,
 * asi que las sumas intermedias no pueden perder precision.
 */
export const MAXIMO_CENTESIMOS = 1_000_000_000_000;
/** Cero. */
export const CERO_CENTESIMOS = 0;
/**
 * Construye un importe validando que sea un entero finito dentro de la cota.
 *
 * La asercion `as Centesimos` es la unica forma de crear la marca, y ocurre despues de
 * validar en tiempo de ejecucion: es el caso que CLAUDE.md §1.1 admite expresamente.
 */
export function crearCentesimos(valor, campo) {
    if (!Number.isInteger(valor)) {
        return fallo(errorDominio('MONTO_INVALIDO', campo));
    }
    if (Math.abs(valor) > MAXIMO_CENTESIMOS) {
        return fallo(errorDominio('DESBORDE_MONETARIO', campo));
    }
    return exito(valor);
}
/**
 * Convierte pesos **enteros** a centesimos. No acepta decimales a proposito: un peso con
 * decimales ya seria un `float`, que es justo lo que este modulo existe para evitar.
 */
export function centesimosDesdePesos(pesos, campo) {
    if (!Number.isInteger(pesos)) {
        return fallo(errorDominio('MONTO_INVALIDO', campo));
    }
    return crearCentesimos(pesos * 100, campo);
}
/** Numero crudo del importe. Para persistir y para pruebas; no para operar. */
export function valorCentesimos(monto) {
    return monto;
}
/** Lanza si el resultado de una operacion interna se sale de la cota. */
function asegurar(valor) {
    if (!Number.isInteger(valor) || Math.abs(valor) > MAXIMO_CENTESIMOS) {
        // Violacion de invariante, no fallo esperable: ADR-034 reserva las excepciones
        // exactamente para esto. Los operandos ya eran Centesimos validos.
        throw new RangeError('DESBORDE_MONETARIO: el resultado excede la cota de seguridad.');
    }
    // Normaliza el cero negativo. `negar(0)` produce -0, que compara igual a 0 con `===`
    // pero distinto con `Object.is` y con las aserciones de las pruebas, y que aparece
    // como "-0" en algunas serializaciones. Un importe cero es uno solo.
    return (valor === 0 ? 0 : valor);
}
/** Suma. Con cero argumentos devuelve cero. */
export function sumar(...montos) {
    let total = 0;
    for (const monto of montos) {
        total += monto;
    }
    return asegurar(total);
}
/** Resta `b` de `a`. */
export function restar(a, b) {
    return asegurar(a - b);
}
/** Cambia el signo. */
export function negar(monto) {
    // `0 - monto` y no `-monto`: la marca de tipo hace que la negacion unaria no sea
    // segura a ojos del compilador, y restar de cero expresa lo mismo sin ambiguedad.
    return asegurar(0 - monto);
}
/** Valor absoluto. */
export function valorAbsoluto(monto) {
    return asegurar(Math.abs(monto));
}
/** Multiplica por un entero. */
export function multiplicarPorEntero(monto, factor) {
    if (!Number.isInteger(factor)) {
        return fallo(errorDominio('MONTO_INVALIDO', 'factor'));
    }
    return crearCentesimos(monto * factor);
}
/** Compara dos importes: -1, 0 o 1. */
export function comparar(a, b) {
    if (a < b)
        return -1;
    if (a > b)
        return 1;
    return 0;
}
/** `a` y `b` son el mismo importe. */
export function sonIguales(a, b) {
    return a === b;
}
/** `a` es estrictamente mayor que `b`. */
export function esMayor(a, b) {
    return a > b;
}
/** `a` es estrictamente menor que `b`. */
export function esMenor(a, b) {
    return a < b;
}
/** El importe es exactamente cero. */
export function esCero(monto) {
    return monto === 0;
}
/** El importe es estrictamente positivo. */
export function esPositivo(monto) {
    return monto > 0;
}
/** El importe es estrictamente negativo. */
export function esNegativo(monto) {
    return monto < 0;
}
/** El menor de los dos. */
export function minimo(a, b) {
    return a <= b ? a : b;
}
/** El mayor de los dos. */
export function maximo(a, b) {
    return a >= b ? a : b;
}
/**
 * Division entera con politica de redondeo explicita. Todo con enteros: `dividendo` y
 * `divisor` son enteros y el resultado tambien.
 */
function dividirRedondeando(dividendo, divisor, politica) {
    const cociente = Math.trunc(dividendo / divisor);
    const resto = dividendo - cociente * divisor;
    if (resto === 0) {
        return cociente;
    }
    // Signo del valor exacto, para saber que significa "arriba" y "abajo".
    const negativo = dividendo < 0 !== divisor < 0;
    const paso = negativo ? -1 : 1;
    switch (politica) {
        case 'haciaCero':
            return cociente;
        case 'lejosDeCero':
            return cociente + paso;
        case 'haciaArriba':
            return negativo ? cociente : cociente + 1;
        case 'haciaAbajo':
            return negativo ? cociente - 1 : cociente;
        case 'mitadLejosDeCero':
        case 'mitadAlPar': {
            const dobleResto = Math.abs(resto) * 2;
            const magnitudDivisor = Math.abs(divisor);
            if (dobleResto > magnitudDivisor) {
                return cociente + paso;
            }
            if (dobleResto < magnitudDivisor) {
                return cociente;
            }
            // Empate exacto en la mitad.
            if (politica === 'mitadLejosDeCero') {
                return cociente + paso;
            }
            return cociente % 2 === 0 ? cociente : cociente + paso;
        }
    }
}
/**
 * Aplica una tasa expresada como fraccion de enteros: `monto * numerador / denominador`.
 *
 * Se usa una fraccion y no un porcentaje decimal para que no aparezca ni un `float`.
 * Un 21 % es `aplicarTasa(monto, 21, 100, 'mitadAlPar')`; un 3,5 % es `(monto, 35, 1000)`.
 */
export function aplicarTasa(monto, numerador, denominador, politica) {
    if (!Number.isInteger(numerador)) {
        return fallo(errorDominio('MONTO_INVALIDO', 'numerador'));
    }
    if (!Number.isInteger(denominador) || denominador === 0) {
        return fallo(errorDominio('DIVISOR_INVALIDO', 'denominador'));
    }
    const producto = monto * numerador;
    if (!Number.isSafeInteger(producto)) {
        return fallo(errorDominio('DESBORDE_MONETARIO', 'monto'));
    }
    return crearCentesimos(dividirRedondeando(producto, denominador, politica));
}
/**
 * Reparte un importe en `partes` iguales **sin perder ni inventar un centesimo**.
 *
 * Usa el metodo del resto mayor: el sobrante se distribuye de a un centesimo entre las
 * primeras partes, de modo que la suma del resultado siempre es exactamente `monto`.
 * Es la propiedad que importa al dividir una cuenta.
 */
export function repartir(monto, partes) {
    if (!Number.isInteger(partes) || partes <= 0) {
        return fallo(errorDominio('DIVISOR_INVALIDO', 'partes'));
    }
    const base = Math.trunc(monto / partes);
    const sobrante = monto - base * partes;
    const paso = sobrante < 0 ? -1 : 1;
    const aRepartir = Math.abs(sobrante);
    const resultado = [];
    for (let indice = 0; indice < partes; indice += 1) {
        resultado.push(asegurar(indice < aRepartir ? base + paso : base));
    }
    return exito(resultado);
}
/**
 * Formatea un importe con la convencion uruguaya: punto para los miles, coma para los
 * decimales. `130050` se convierte en `1.300,50`.
 *
 * Se formatea a mano y no con `Intl` a proposito: el resultado de `Intl` cambia entre
 * versiones de ICU, y un importe que se ve distinto segun la maquina es inaceptable en
 * un comprobante.
 */
export function formatear(monto, opciones) {
    const negativo = monto < 0;
    const absoluto = Math.abs(monto);
    const enteros = Math.trunc(absoluto / 100);
    const decimales = absoluto - enteros * 100;
    const textoEnteros = String(enteros).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const textoDecimales = String(decimales).padStart(2, '0');
    const signo = negativo ? '-' : '';
    const simbolo = opciones.conSimbolo ? '$ ' : '';
    return `${signo}${simbolo}${textoEnteros},${textoDecimales}`;
}
//# sourceMappingURL=centesimos.js.map